import React, { useCallback, useContext, useMemo, useState } from "react";
import { useFragment, useRefetchableFragment } from "react-relay";

import { graphql } from "relay-runtime";

import { trackConversion } from "~/lib/ab/growthbook";
import { useEffectAfterMount } from "~/lib/hooks";
import { RecordIconSwitch } from "~/meetingRecorder/RecordIcon";
import MeetingPhaseContext from "~/stream/MeetingPhaseContext";
import { setSchedulingMeetingAssistantConfigValue } from "~/stream/StreamSettings";
import { autoShareIsSetToEveryone, enableAutoShareToEveryone } from "~/stream/SuggestedActions/AutoSendToEveryone";

import { FLOATING_RECORDING_CONTROLS_EXPERIMENT_KEY } from "./FloatingRecordingControls";
import { ControlButton } from "./SharedComponents";

import type { AutoRecordToggle_AutoRecordIsOnForStream as AutoRecordIsOnForStream } from "~/__generated__/AutoRecordToggle_AutoRecordIsOnForStream.graphql";
import type { AutoRecordToggle_AutoRecordReason as AutoRecordReason } from "~/__generated__/AutoRecordToggle_AutoRecordReason.graphql";
import type { AutoRecordToggle_note$key } from "~/__generated__/AutoRecordToggle_note.graphql";
import type { AutoRecordToggle_refetchableAutoRecordIsOnForStream$key } from "~/__generated__/AutoRecordToggle_refetchableAutoRecordIsOnForStream.graphql";
import type { AutoRecordToggle_refetchableAutoRecordReason$key } from "~/__generated__/AutoRecordToggle_refetchableAutoRecordReason.graphql";
import type { AutoRecordToggle_viewer$key } from "~/__generated__/AutoRecordToggle_viewer.graphql";

const viewerFragment = graphql`
	fragment AutoRecordToggle_viewer on Viewer {
		meetingRecapAutoShareSettings {
			sendTo
		}
	}
`;

const noteFragment = graphql`
	fragment AutoRecordToggle_note on Note {
		stream {
			id
			...AutoRecordToggle_refetchableAutoRecordReason
			...AutoRecordToggle_refetchableAutoRecordIsOnForStream
		}
		event {
			hasUsersWithExternalEmail
			willBeRecorded
		}
		...AutoSendToEveryone_autoShareIsSetToEveryone_note
	}
`;

const refetchableAutoRecordReasonFragment = graphql`
	fragment AutoRecordToggle_refetchableAutoRecordReason on NoteStream
	@refetchable(queryName: "AutoRecordToggle_AutoRecordReason") {
		...StreamAutoRecordAutomationConfig_useAutoRecordReason_stream
	}
`;

const refetchableAutoRecordIsOnForStreamFragment = graphql`
	fragment AutoRecordToggle_refetchableAutoRecordIsOnForStream on NoteStream
	@refetchable(queryName: "AutoRecordToggle_AutoRecordIsOnForStream") {
		schedulingMeetingAssistantAutomationIsActive
	}
`;

export default function AutoRecordToggle({
	viewer: viewerKey,
	note: noteKey,
	onMouseEnterTarget,
	onMouseLeaveTarget,
}: {
	viewer: AutoRecordToggle_viewer$key;
	note: AutoRecordToggle_note$key;
	onMouseEnterTarget: (event: React.MouseEvent<HTMLElement>) => void;
	onMouseLeaveTarget: () => void;
}) {
	const { phase, getTimerValue } = useContext(MeetingPhaseContext);

	const [isAutoRecordToggleLoading, setIsAutoRecordToggleLoading] = useState(false);
	const [optimisticAutoRecordIsOn, setOptimisticAutoRecordIsOn] = useState(false);

	const viewer = useFragment(viewerFragment, viewerKey);
	const note = useFragment(noteFragment, noteKey);

	const [_, refetchAutoRecordReason] = useRefetchableFragment<
		AutoRecordReason,
		AutoRecordToggle_refetchableAutoRecordReason$key
	>(refetchableAutoRecordReasonFragment, note.stream);

	const [streamAutoRecordIsOn, refetchAutoRecordIsOnForStream] = useRefetchableFragment<
		AutoRecordIsOnForStream,
		AutoRecordToggle_refetchableAutoRecordIsOnForStream$key
	>(refetchableAutoRecordIsOnForStreamFragment, note.stream);

	const autoSendToEveryoneIsOn = autoShareIsSetToEveryone(note);

	const autoRecordIsOn = streamAutoRecordIsOn.schedulingMeetingAssistantAutomationIsActive;
	const displayChecked = isAutoRecordToggleLoading ? optimisticAutoRecordIsOn : autoRecordIsOn;

	useEffectAfterMount(() => {
		refetchAutoRecordIsOnForStream({}, { fetchPolicy: "store-and-network" });
	}, [note.event?.willBeRecorded, refetchAutoRecordIsOnForStream]);

	const onToggleAutoRecord = useCallback(async () => {
		if (isAutoRecordToggleLoading) return;
		trackConversion(FLOATING_RECORDING_CONTROLS_EXPERIMENT_KEY);
		const newValue = !autoRecordIsOn;

		setOptimisticAutoRecordIsOn(newValue);
		setIsAutoRecordToggleLoading(true);

		try {
			await setSchedulingMeetingAssistantConfigValue(note.stream.id, newValue);
			if (
				newValue === true &&
				note.event?.hasUsersWithExternalEmail &&
				!autoSendToEveryoneIsOn &&
				viewer.meetingRecapAutoShareSettings?.sendTo == "ALL"
			) {
				await enableAutoShareToEveryone(note.stream.id);
			}
			refetchAutoRecordReason({}, { fetchPolicy: "store-and-network" });
		} finally {
			setIsAutoRecordToggleLoading(false);
		}
	}, [
		isAutoRecordToggleLoading,
		autoSendToEveryoneIsOn,
		note.event?.hasUsersWithExternalEmail,
		note.stream.id,
		autoRecordIsOn,
		refetchAutoRecordReason,
		viewer.meetingRecapAutoShareSettings?.sendTo,
	]);

	const toggleText = useMemo(() => {
		const minutesUntilBotJoins = Math.max(0, Math.ceil(getTimerValue() / 60));

		if (autoRecordIsOn && phase === "before" && minutesUntilBotJoins <= 5) {
			return `Joining in ${minutesUntilBotJoins} min…`;
		}

		return `Auto-record is ${autoRecordIsOn ? "on" : "off"}`;
	}, [autoRecordIsOn, phase, getTimerValue]);

	return (
		<ControlButton
			aria-pressed={displayChecked}
			onClick={onToggleAutoRecord}
			data-test-id="auto-record-in-note-header"
			onMouseEnter={onMouseEnterTarget}
			onMouseLeave={onMouseLeaveTarget}
		>
			<RecordIconSwitch aria-hidden checked={displayChecked} rootElement="div" />
			&ensp;{toggleText}
		</ControlButton>
	);
}
